import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Info, X, Euro } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from './LanguageContext';
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-317">
      <div className="flex items-center justify-between" data-id="element-318">
        <label className="text-sm font-medium text-slate-600" data-id="element-319">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-320" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-321" />
    </div>;
}
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const {
    t
  } = useLanguage();
  const [products, setProducts] = useState(10);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(4);
  const [countries, setCountries] = useState(10);
  const [benchmarkOpen, setBenchmarkOpen] = useState(false);
  const [costBenchmarkOpen, setCostBenchmarkOpen] = useState(false);
  // List-price unit economics
  const masterSnakCostBase = 2900;
  const masterSnakCostFloor = 1900;
  const campaignDuration = 3;
  const localizationCostBase = 999;
  const monthlyInsightsFeeBase = 99;
  const durationRebate = 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  // --- Volume discounts ---
  // 1) Country localization: 2 countries → 0% off (€999). 50+ countries → 50% off (€500). Linear.
  const COUNTRY_DISCOUNT_FLOOR = 2;
  const COUNTRY_DISCOUNT_CEILING = 50;
  const COUNTRY_DISCOUNT_MAX = 0.5;
  const countryDiscount = Math.min(COUNTRY_DISCOUNT_MAX, Math.max(0, (countries - COUNTRY_DISCOUNT_FLOOR) / (COUNTRY_DISCOUNT_CEILING - COUNTRY_DISCOUNT_FLOOR) * COUNTRY_DISCOUNT_MAX));
  const effectiveCountryCost = localizationCostBase * (1 - countryDiscount);
  const countryDeploymentCost = effectiveCountryCost;
  // 2) Master Snak: drops from €2,900 → €1,900 as total annual campaigns (masters) grow from 1 → 100+.
  const totalAnnualCampaigns = products * campaignsPerProduct;
  const MASTER_DISCOUNT_FLOOR = 1;
  const MASTER_DISCOUNT_CEILING = 100;
  const masterProgress = Math.min(1, Math.max(0, (totalAnnualCampaigns - MASTER_DISCOUNT_FLOOR) / (MASTER_DISCOUNT_CEILING - MASTER_DISCOUNT_FLOOR)));
  const effectiveMasterSnakCost = masterSnakCostBase - (masterSnakCostBase - masterSnakCostFloor) * masterProgress;
  const masterSnakCost = effectiveMasterSnakCost;
  // 3) Data insights & hosting (monthly): 2 countries → €99/mo. 50+ countries → 50% off (€49.50/mo). Linear.
  const INSIGHTS_DISCOUNT_FLOOR = 2;
  const INSIGHTS_DISCOUNT_CEILING = 50;
  const INSIGHTS_DISCOUNT_MAX = 0.5;
  const insightsDiscount = Math.min(INSIGHTS_DISCOUNT_MAX, Math.max(0, (countries - INSIGHTS_DISCOUNT_FLOOR) / (INSIGHTS_DISCOUNT_CEILING - INSIGHTS_DISCOUNT_FLOOR) * INSIGHTS_DISCOUNT_MAX));
  const effectiveMonthlyInsightsFee = monthlyInsightsFeeBase * (1 - insightsDiscount);
  const monthlyInsightsFee = effectiveMonthlyInsightsFee;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costPerCampaign,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-322">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-323">
        {/* Header */}
        <div className="mb-6" data-id="element-324">
          <div className="flex items-center gap-3 mb-1" data-id="element-325">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-326">
              Snakker
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-327">
            Fast Marketing<sup className="text-[0.6em] font-medium" data-id="element-328">™</sup>{' '}
            {t('Cost Calculator', 'Omkostningsberegner')}
          </h1>
          <p className="text-base text-slate-500 mt-1" data-id="element-329">
            {t('Estimate your annual campaign operating cost', 'Estimer dine årlige driftsomkostninger for kampagner')}
          </p>
          <p className="text-xs text-slate-400 mt-0.5" data-id="element-330">
            {t('Adjust the variables to see how your portfolio scales across brands, campaigns, countries, and insights.', 'Juster variablerne for at se, hvordan din portefølje skalerer på tværs af brands, kampagner, lande og indsigter.')}
          </p>
          {/* Industry benchmark + Cost benchmark popovers */}
          <div className="mt-3 flex flex-wrap items-start gap-2" data-id="element-331">
            <div className="relative inline-block" data-id="element-332">
              <button type="button" onClick={() => {
              setBenchmarkOpen(v => !v);
              setCostBenchmarkOpen(false);
            }} aria-expanded={benchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-333">
                <Info className="w-3.5 h-3.5" data-id="element-334" />
                {t('Industry benchmark', 'Branchebenchmark')}
              </button>
              <AnimatePresence data-id="element-335">
                {benchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[380px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-336">
                    <div className="flex items-start justify-between gap-3" data-id="element-337">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-338">
                        {t('Big Pharma at a glance', 'Big Pharma overblik')}
                      </p>
                      <button type="button" onClick={() => setBenchmarkOpen(false)} aria-label="Close benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-339">
                        <X className="w-3.5 h-3.5" data-id="element-340" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-341">
                      {t('A typical big-pharma marketer operates across', 'En typisk big-pharma markedsfører opererer på tværs af')}{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-342">
                        {t('100–135 countries', '100–135 lande')}
                      </span>
                      , {t('promoting', 'og promoverer')}{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-343">
                        {t('20–60 prescription brands', '20–60 receptpligtige brands')}
                      </span>{' '}
                      — {t('with', 'med')}{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-344">
                        {t('4–6 campaigns per brand, per country, per year', '4–6 kampagner pr. brand, pr. land, pr. år')}
                      </span>
                      .
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-345">
                      {t('Use the sliders below to model your own portfolio against this scale.', 'Brug skyderne nedenfor til at modellere din egen portefølje mod denne skala.')}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="relative inline-block" data-id="element-346">
              <button type="button" onClick={() => {
              setCostBenchmarkOpen(v => !v);
              setBenchmarkOpen(false);
            }} aria-expanded={costBenchmarkOpen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#6C2BD9]/[0.07] hover:bg-[#6C2BD9]/[0.12] border border-[#6C2BD9]/20 text-xs font-semibold text-[#6C2BD9] transition-colors" data-id="element-347">
                <Euro className="w-3.5 h-3.5" data-id="element-348" />
                {t('Cost benchmark', 'Omkostningsbenchmark')}
              </button>
              <AnimatePresence data-id="element-349">
                {costBenchmarkOpen && <motion.div initial={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -4,
                scale: 0.98
              }} transition={{
                duration: 0.15
              }} className="absolute z-20 mt-2 w-[320px] md:w-[420px] bg-white rounded-xl border-2 border-[#6C2BD9]/20 shadow-xl shadow-[#6C2BD9]/10 p-4" role="dialog" data-id="element-350">
                    <div className="flex items-start justify-between gap-3" data-id="element-351">
                      <p className="text-[11px] font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-352">
                        {t('Cost Benchmark — Digital Pharma Campaigns', 'Omkostningsbenchmark — Digitale Pharma-kampagner')}
                      </p>
                      <button type="button" onClick={() => setCostBenchmarkOpen(false)} aria-label="Close cost benchmark" className="text-slate-400 hover:text-slate-600 transition-colors -mt-0.5" data-id="element-353">
                        <X className="w-3.5 h-3.5" data-id="element-354" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed mt-2" data-id="element-355">
                      {t('A typical digital quarterly campaign for a legacy product in a country — banners, emails, WhatsApp GIFs — including strategy, creative & compliant copy, design / production, regulatory review, and local adaptation costs', 'En typisk digital kvartalskampagne for et legacy-produkt i et land — bannere, e-mails, WhatsApp-GIFs — inklusiv strategi, kreativ & compliant tekst, design / produktion, regulatorisk gennemgang og lokal tilpasning koster')}{' '}
                      <span className="font-semibold text-[#6C2BD9]" data-id="element-356">
                        {t('€15,000 – €19,000 per campaign', '€15.000 – €19.000 pr. kampagne')}
                      </span>
                      .
                    </p>
                    <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-357">
                      {t('Compare against the Snakker cost per localized deployment shown on the right.', 'Sammenlign med Snakker-omkostningen pr. lokaliseret udrulning vist til højre.')}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-358">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-359">
            <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-5 space-y-4 border-2 border-[#6C2BD9]/20" data-id="element-360">
              <div data-id="element-361">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-362">
                  {t('Unit Economics', 'Enhedsøkonomi')}
                </h3>
                <p className="text-[11px] text-[#6C2BD9]/60 mt-0.5" data-id="element-363">
                  {t('Fixed Snakker pricing — everything below scales from these unit costs.', 'Faste Snakker-priser — alt nedenfor skalerer fra disse enhedsomkostninger.')}
                </p>
              </div>

              {/* Master Snak */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-364">
                <div className="flex items-center justify-between" data-id="element-365">
                  <div data-id="element-366">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-367">
                      {t('Master Snak', 'Master Snak')}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-368">
                      {t('Per campaign master creation', 'Pr. kampagne master-skabelse')}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-369">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-370">
                      €{Math.round(effectiveMasterSnakCost).toLocaleString()}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-371">
                  {t('Volume benefit: drops from €2,900 to €1,900 as total campaigns / year scales from 1 to 100+.', 'Volumenfordel: falder fra €2.900 til €1.900 når totale kampagner / år skalerer fra 1 til 100+.')}
                </p>
              </div>

              {/* Cost per country localization */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-372">
                <div className="flex items-center justify-between" data-id="element-373">
                  <div data-id="element-374">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-375">
                      {t('Cost per country localization', 'Omkostning pr. landelokalisering')}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-376">
                      {t('One-time set-up per country', 'Engangsopsætning pr. land')}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-377">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-378">
                      €{Math.round(effectiveCountryCost)}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-379">
                  {t('Volume benefit: discount slides from 0% at 2 countries up to 50% at 50+ countries.', 'Volumenfordel: rabat glider fra 0% ved 2 lande op til 50% ved 50+ lande.')}
                </p>
              </div>

              {/* Data insights & hosting */}
              <div className="rounded-xl bg-white border-2 border-[#6C2BD9]/20 p-4" data-id="element-380">
                <div className="flex items-center justify-between" data-id="element-381">
                  <div data-id="element-382">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-383">
                      {t('Data insights & hosting', 'Dataindsigter & hosting')}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-384">
                      {t('Per country', 'Pr. land')}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-385">
                    <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-386">
                      €{effectiveMonthlyInsightsFee.toFixed(0)}
                      <span className="text-xs font-normal text-slate-400 ml-1" data-id="element-387">
                        / {t('month', 'måned')}
                      </span>
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed" data-id="element-388">
                  {t('Volume benefit: discount slides from 0% at 2 countries up to 50% at 50+ countries.', 'Volumenfordel: rabat glider fra 0% ved 2 lande op til 50% ved 50+ lande.')}
                </p>
              </div>
            </div>

            {/* Portfolio Scale */}
            <div className="bg-slate-50 rounded-2xl p-5 space-y-4 border border-slate-200" data-id="element-389">
              <div data-id="element-390">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest" data-id="element-391">
                  {t('Portfolio Scale', 'Porteføljeskala')}
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-392">
                  {t('Adjust your portfolio size to see how cost scales.', 'Juster din porteføljestørrelse for at se, hvordan omkostningerne skalerer.')}
                </p>
              </div>
              <SliderInput label={t('Number of Brands', 'Antal brands')} value={products} onChange={setProducts} min={1} max={40} data-id="element-393" />
              <SliderInput label={t('Campaigns per Year', 'Kampagner pr. år')} value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={2} max={6} data-id="element-394" />
              <SliderInput label={t('Number of Countries', 'Antal lande')} value={countries} onChange={setCountries} min={2} max={75} data-id="element-395" />
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-396">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-397">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-398">
                {t('Cost per Localized Campaign', 'Omkostning pr. lokaliseret kampagne')}
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-399">
                {formatEuro(calc.costPerDeployment)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-400">
                {t('per localized deployment', 'pr. lokaliseret udrulning')}
              </p>
            </div>

            {/* Volume + total annual */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-401">
              <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest mb-3" data-id="element-402">
                {t('Campaign Volume', 'Kampagnevolumen')}
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-403">
                <div data-id="element-404">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-405">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-406">
                    {t('master campaigns / year', 'master-kampagner / år')}
                  </p>
                </div>
                <div data-id="element-407">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-408">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-409">
                    {t('localized deployments / year', 'lokaliserede udrulninger / år')}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-baseline justify-between" data-id="element-410">
                <p className="text-xs text-slate-500" data-id="element-411">
                  {t('Snakker annual revenue', 'Snakker årlig omsætning')}
                </p>
                <p className="text-2xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-412">
                  {formatEuro(calc.totalAnnualCost)}
                  <span className="text-xs text-slate-400 font-normal ml-1" data-id="element-413">
                    / {t('year', 'år')}
                  </span>
                </p>
              </div>
            </div>

            {/* Breakdown */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200" data-id="element-414">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3" data-id="element-415">
                {t('Cost Breakdown', 'Omkostningsnedbrydning')}
              </p>
              <div className="space-y-2" data-id="element-416">
                <div className="flex justify-between items-center" data-id="element-417">
                  <span className="text-sm text-slate-600" data-id="element-418">
                    {t('Avg. cost per campaign', 'Gns. omkostning pr. kampagne')}
                  </span>
                  <span className="text-sm font-semibold text-slate-900" data-id="element-419">
                    {formatEuro(calc.costPerCampaign)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-1" data-id="element-420" />
                {calc.firstCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-421">
                    <span className="text-sm text-slate-500" data-id="element-422">
                      {t('New masters', 'Nye masters')} ({calc.firstCampaigns} ×{' '}
                      {formatEuro(masterSnakCost)})
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-423">
                      {formatEuro(calc.firstCampaigns * masterSnakCost)}
                    </span>
                  </div>}
                {calc.followUpCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-424">
                    <span className="text-sm text-slate-500" data-id="element-425">
                      {t('Follow-up campaigns', 'Opfølgningskampagner')} (
                      {calc.followUpCampaigns} × €1,000)
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-426">
                      {formatEuro(calc.followUpCampaigns * FOLLOW_UP_MASTER_COST)}
                    </span>
                  </div>}
                <div className="flex justify-between items-center" data-id="element-427">
                  <span className="text-sm text-slate-500" data-id="element-428">
                    {t('Localization & set-up', 'Lokalisering & opsætning')}
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-429">
                    {formatEuro(calc.annualDeploymentCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-430">
                  <span className="text-sm text-slate-500" data-id="element-431">
                    {t('Hosting & data insights', 'Hosting & dataindsigter')} (
                    {campaignDuration}
                    {t('mo', 'mdr')})
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-432">
                    {formatEuro(calc.annualInsightsCost)}
                  </span>
                </div>
              </div>
            </div>

            {/* Strategic callout */}
            <div className="bg-[#6C2BD9]/5 rounded-2xl p-4 border border-[#6C2BD9]/10" data-id="element-433">
              <p className="text-sm font-medium text-[#6C2BD9] leading-relaxed" data-id="element-434">
                {t('This is not software pricing. This is the annual cost of a fully managed campaign operating model across your portfolio.', 'Dette er ikke softwarepriser. Dette er de årlige omkostninger for en fuldt administreret kampagnedriftsmodel på tværs af din portefølje.')}
              </p>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-435">
              {t('As you scale across more products, campaigns, or countries, cost grows predictably — while execution stays centralized.', 'Når du skalerer på tværs af flere produkter, kampagner eller lande, vokser omkostningerne forudsigeligt — mens eksekveringen forbliver centraliseret.')}
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-436">
          <Button onClick={onContinue} data-id="element-437">
            {t('Financials', 'Finansielle tal')}
          </Button>
        </div>
      </div>
    </div>;
}